import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { db } from "../firebase";
import { setLoading } from "../redux/loaderSlice";
import { formatCurrencyINR } from "../utils/calculations";
import { useNavigate } from "react-router-dom";

/* ================= STYLES ================= */

const Wrapper = styled.div`
  padding: 20px;
  margin-top: 60px;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
`;

const HeaderSection = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  h1 {
    font-size: 28px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.textPrimary};
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 15px;
    margin: 0;
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: 16px;
  border-radius: ${({ theme }) => theme.spacing.radius};
  box-shadow: ${({ theme }) => theme.shadow};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const CardTitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const CardValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-top: 8px;
  color: ${({ $tone, theme }) =>
    $tone === "success"
      ? theme.colors.success
      : $tone === "danger"
        ? theme.colors.danger
        : theme.colors.textPrimary};
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: 16px;
  border-radius: ${({ theme }) => theme.spacing.radius};
  box-shadow: ${({ theme }) => theme.shadow};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 16px 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 260px) auto;
  gap: 12px;
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InvestmentForms = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormCard = styled.div`
  padding: 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};

  h3 {
    margin: 0 0 12px 0;
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 16px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-weight: 600;
  }
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.12);
  }
`;

const Button = styled.button`
  border: none;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  padding: 11px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  min-height: 42px;

  &:hover {
    opacity: 0.92;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const HelperText = styled.p`
  margin: 12px 0 0 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;

  label {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-weight: 600;
  }
`;

const Select = styled.select`
  padding: 9px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 15px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.spacing.radius};
  border: 1px dashed ${({ theme }) => theme.colors.border};
`;

const DaySection = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme, $isToday }) =>
    $isToday ? theme.colors.accent : theme.colors.border};
  border-radius: ${({ theme }) => theme.spacing.radius};
  box-shadow: ${({ theme }) => theme.shadow};
  overflow: hidden;
  margin-bottom: 18px;
`;

const DayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: ${({ theme, $isToday }) =>
    $isToday ? "rgba(212, 175, 55, 0.16)" : theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-wrap: wrap;

  h3 {
    margin: 0;
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 17px;
  }

  span {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 13px;
  }
`;

const DayMetrics = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const MetricPill = styled.div`
  padding: 7px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 600;

  strong {
    color: ${({ $tone, theme }) =>
      $tone === "success"
        ? theme.colors.success
        : $tone === "danger"
          ? theme.colors.danger
          : theme.colors.textPrimary};
    margin-left: 4px;
  }
`;

const TableContainer = styled.div`
  padding: 16px;
  overflow-x: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 920px;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 8px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 8px;
  font-size: 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const TypeBadge = styled.span`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;

  ${({ $variant }) =>
    $variant === "inward" &&
    `
    background: #DCFCE7;
    color: #166534;
  `}

  ${({ $variant }) =>
    $variant === "outward" &&
    `
    background: #FEE2E2;
    color: #991B1B;
  `}
`;

const MobileContainer = styled.div`
  display: none;
  padding: 12px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const MobileCard = styled.div`
  background: ${({ theme }) => theme.colors.background};
  padding: 14px;
  border-radius: ${({ theme }) => theme.spacing.radius};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 12px;
  font-weight: 500;
`;

const Val = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: right;
`;

/* ================= HELPERS ================= */

const INVENTORY_COLLECTION = "inventoryDaily";
const INVENTORY_OPENING_DOC_ID = "openingInventoryValue";

const getTimestampDate = (raw) => {
  if (!raw) return null;
  if (typeof raw?.toDate === "function") return raw.toDate();
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toDateKey = (raw) => {
  const date = raw instanceof Date ? raw : getTimestampDate(raw);
  if (!date || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateKey = (dateKey) => {
  if (!dateKey) return null;
  const [year, month, day] = dateKey.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDisplayDate = (dateKey) => {
  const date = parseDateKey(dateKey);
  if (!date) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const todayKey = () => toDateKey(new Date());

const normaliseAmountInput = (value) => value.replace(/[^\d.]/g, "");

const isValidDateKey = (dateKey) => Boolean(parseDateKey(dateKey));

const calculateInventoryTotal = (opening, inward, outward) =>
  (Number(opening) || 0) + (Number(inward) || 0) - (Number(outward) || 0);

/** Recompute day totals and running balances for a subset of transactions (e.g. search filter). */
const rebuildDayGroupFromTransactions = (group, transactions) => {
  let inwardTotal = 0;
  let outwardTotal = 0;
  let runningBalance = group.opening;
  const decorated = transactions.map((row) => {
    const inward = Number(row.inward) || 0;
    const outward = Number(row.outward) || 0;
    inwardTotal += inward;
    outwardTotal += outward;
    runningBalance += inward - outward;
    return { ...row, runningBalance };
  });
  return {
    ...group,
    transactions: decorated,
    inwardTotal,
    outwardTotal,
    closing: calculateInventoryTotal(group.opening, inwardTotal, outwardTotal),
  };
};

const isDateWithinFilter = (dateKey, filter, selectedDate, customStart, customEnd) => {
  const date = parseDateKey(dateKey);
  if (!date) return false;

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  if (filter === "today") {
    return dateKey === toDateKey(startOfToday);
  }

  if (filter === "month") {
    return date >= startOfMonth && date <= endOfMonth;
  }

  if (filter === "date") {
    return selectedDate ? dateKey === selectedDate : true;
  }

  if (filter === "custom") {
    const start = customStart ? parseDateKey(customStart) : null;
    const end = customEnd ? parseDateKey(customEnd) : start;
    if (start && date < start) return false;
    if (end && date > end) return false;
  }

  return true;
};

/* ================= COMPONENT ================= */

export default function Inventory() {
  const dispatch = useDispatch();
  const [bills, setBills] = useState([]);
  const [openingInventoryValue, setOpeningInventoryValue] = useState(0);
  const [investmentPostValue, setInvestmentPostValue] = useState("");
  const [investmentEditValue, setInvestmentEditValue] = useState("");
  const [savingAction, setSavingAction] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const fetchInventoryData = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const billingQuery = query(collection(db, "billing"), orderBy("createdAt", "desc"));
      const [billingSnap, inventorySnap] = await Promise.all([
        getDocs(billingQuery),
        getDocs(collection(db, INVENTORY_COLLECTION)),
      ]);

      setBills(billingSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      let savedOpeningValue = 0;
      let hasGlobalOpeningValue = false;
      const legacyOpeningEntries = [];

      inventorySnap.docs.forEach((entryDoc) => {
        const data = entryDoc.data() || {};
        if (entryDoc.id === INVENTORY_OPENING_DOC_ID) {
          savedOpeningValue = Number(data.openingValue) || 0;
          hasGlobalOpeningValue = true;
          return;
        }

        const dateKey = data.dateKey || entryDoc.id;
        if (!isValidDateKey(dateKey)) return;

        legacyOpeningEntries.push({
          dateKey,
          openingValue: Number(data.openingValue) || 0,
          updatedAt: data.updatedAt || null,
        });
      });

      if (!hasGlobalOpeningValue && legacyOpeningEntries.length > 0) {
        savedOpeningValue = legacyOpeningEntries.sort((a, b) =>
          b.dateKey.localeCompare(a.dateKey)
        )[0].openingValue;
      }

      setOpeningInventoryValue(savedOpeningValue);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load inventory data");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  }, [dispatch]);

  useEffect(() => {
    fetchInventoryData();
  }, [fetchInventoryData]);

  const transactions = useMemo(() => {
    const rows = [];

    bills.forEach((bill) => {
      const form = bill.form || {};
      const loanId = bill.customerId || form.customerId || bill.id;
      const customerName = form.customerName || "-";
      const created = getTimestampDate(bill.createdAt) || new Date(0);
      const disbursed = Number(form.loanAmount) || 0;
      let seq = 0;

      const pushRow = (type, amount, particulars, dateOverride) => {
        const date = dateOverride || created;
        const dateKey = toDateKey(date);
        if (!dateKey || amount <= 0) return;

        seq += 1;
        rows.push({
          id: `${bill.id}-${type}-${seq}`,
          billId: bill.id,
          date,
          dateKey,
          loanId,
          customerName,
          type,
          particulars,
          inward: type === "Inward" ? amount : null,
          outward: type === "Outward" ? amount : null,
          sortTie: seq,
        });
      };

      pushRow("Outward", disbursed, "Loan Disbursed", created);

      const payments = Array.isArray(bill.payments) ? bill.payments : [];
      let principalRecovered = 0; // newly added

      payments.forEach((payment) => {
        const amount = Number(payment?.amount) || 0;
        const paymentDate = getTimestampDate(payment?.date) || created;
        // const particulars = payment?.isClosing ? "Loan Closure Repayment" : "Repayment Received";
        // pushRow("Inward", amount, particulars, paymentDate);
        // --NEW--
        const principalAmount = Math.min(amount, Math.max(disbursed - principalRecovered, 0));
        principalRecovered += principalAmount;

        const particulars = payment?.isClosing
          ? "Loan Closure Principal Repayment"
          : "Principal Repayment Received";
        pushRow("Inward", principalAmount, particulars, paymentDate);
        // --NEW--
      });
    });

    return rows.sort((a, b) => {
      const ta = a.date.getTime();
      const tb = b.date.getTime();
      if (ta !== tb) return ta - tb;
      if (a.billId !== b.billId) return String(a.billId).localeCompare(String(b.billId));
      return a.sortTie - b.sortTie;
    });
  }, [bills]);

  const dailyRegister = useMemo(() => {
    const dateKeys = new Set();
    transactions.forEach((row) => dateKeys.add(row.dateKey));

    const sortedDateKeys = Array.from(dateKeys).sort((a, b) => a.localeCompare(b));
    let previousClosing = openingInventoryValue;

    return sortedDateKeys.map((dateKey, index) => {
      const opening = previousClosing;
      let runningBalance = opening;
      let inwardTotal = 0;
      let outwardTotal = 0;

      const dayTransactions = transactions
        .filter((row) => row.dateKey === dateKey)
        .map((row) => {
          const inward = Number(row.inward) || 0;
          const outward = Number(row.outward) || 0;
          inwardTotal += inward;
          outwardTotal += outward;
          runningBalance += inward - outward;
          return { ...row, runningBalance };
        });

      const group = {
        dateKey,
        opening,
        inwardTotal,
        outwardTotal,
        closing: calculateInventoryTotal(opening, inwardTotal, outwardTotal),
        isFirstDay: index === 0,
        transactions: dayTransactions,
      };

      previousClosing = group.closing;
      return group;
    });
  }, [openingInventoryValue, transactions]);

  const filteredDailyRegister = useMemo(() => {
    const queryText = search.trim().toLowerCase();

    return dailyRegister
      .filter((group) =>
        isDateWithinFilter(group.dateKey, dateFilter, selectedDate, customStart, customEnd)
      )
      .map((group) => {
        if (!queryText) return group;

        const filteredTransactions = group.transactions.filter((row) =>
          String(row.loanId || "").toLowerCase().includes(queryText) ||
          String(row.customerName || "").toLowerCase().includes(queryText) ||
          String(row.particulars || "").toLowerCase().includes(queryText)
        );

        return rebuildDayGroupFromTransactions(group, filteredTransactions);
      })
      .filter((group) => !queryText || group.transactions.length > 0)
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  }, [dailyRegister, dateFilter, selectedDate, customStart, customEnd, search]);

  const summary = useMemo(() => {
    return filteredDailyRegister.reduce(
      (acc, group) => {
        acc.totalInward += group.inwardTotal;
        acc.totalOutward += group.outwardTotal;
        return acc;
      },
      {
        totalInward: 0,
        totalOutward: 0,
      }
    );
  }, [filteredDailyRegister]);

  const inventoryTotals = useMemo(
    () =>
      transactions.reduce(
        (acc, row) => {
          acc.totalInward += Number(row.inward) || 0;
          acc.totalOutward += Number(row.outward) || 0;
          return acc;
        },
        { totalInward: 0, totalOutward: 0 }
      ),
    [transactions]
  );

  const totalInventoryValue = calculateInventoryTotal(
    openingInventoryValue,
    inventoryTotals.totalInward,
    inventoryTotals.totalOutward
  );

  useEffect(() => {
    setInvestmentEditValue(String(totalInventoryValue));
  }, [totalInventoryValue]);

  const saveInvestmentValue = async (nextInvestmentValue, successMessage) => {
    dispatch(setLoading(true));
    try {
      await setDoc(doc(db, INVENTORY_COLLECTION, INVENTORY_OPENING_DOC_ID), {
        openingValue: nextInvestmentValue,
        updatedAt: serverTimestamp(),
      });

      setOpeningInventoryValue(nextInvestmentValue);
      setInvestmentEditValue(
        String(
          calculateInventoryTotal(
            nextInvestmentValue,
            inventoryTotals.totalInward,
            inventoryTotals.totalOutward
          )
        )
      );

      toast.success(successMessage);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save investment value");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePostInvestmentValue = async (event) => {
    event.preventDefault();

    const postValue = Number(investmentPostValue);
    if (investmentPostValue === "" || Number.isNaN(postValue) || postValue <= 0) {
      toast.error("Enter a valid investment amount to post");
      return;
    }

    setSavingAction("post");
    try {
      const nextInvestmentValue = openingInventoryValue + postValue;
      await saveInvestmentValue(nextInvestmentValue, "Investment amount posted");
      setInvestmentPostValue("");
    } finally {
      setSavingAction("");
    }
  };

  const handleEditInvestmentValue = async (event) => {
    event.preventDefault();

    const editedValue = Number(investmentEditValue);
    if (investmentEditValue === "" || Number.isNaN(editedValue) || editedValue < 0) {
      toast.error("Enter a valid investment value");
      return;
    }

    setSavingAction("edit");
    try {
      const nextInvestmentValue =
        editedValue - inventoryTotals.totalInward + inventoryTotals.totalOutward;
      await saveInvestmentValue(nextInvestmentValue, "Investment value updated");
    } finally {
      setSavingAction("");
    }
  };

  const kpiItems = [
    { title: "Investment Left", value: formatCurrencyINR(totalInventoryValue) },
    { title: "Total Inward", value: formatCurrencyINR(summary.totalInward), tone: "success" },
    { title: "Total Outward", value: formatCurrencyINR(summary.totalOutward), tone: "danger" },
    { title: "Investment Value", value: formatCurrencyINR(openingInventoryValue) },
  ];

  return (
    <Wrapper>
      <HeaderSection>
        <h1>Investment</h1>
        {/* <p>Daily cash/value register tracking inward, outward, and closing balance.</p> */}

        <Button onClick={() => navigate("/accounting")}>Back</Button>
      </HeaderSection>

      <Cards>
        {kpiItems.map((item) => (
          <Card key={item.title}>
            <CardTitle>{item.title}</CardTitle>
            <CardValue $tone={item.tone}>{item.value}</CardValue>
          </Card>
        ))}
      </Cards>

      <Panel>
        <SectionTitle>Investment Value</SectionTitle>
        <InvestmentForms>
          <FormCard>
            <h3>Add Investment</h3>
            <form onSubmit={handlePostInvestmentValue}>
              <FormGrid>
                <Field>
                  <label htmlFor="investment-post-value">Additional Investment Value</label>
                  <Input
                    id="investment-post-value"
                    inputMode="decimal"
                    value={investmentPostValue}
                    placeholder="Enter amount to add"
                    onChange={(event) =>
                      setInvestmentPostValue(normaliseAmountInput(event.target.value))
                    }
                  />
                </Field>
                <Button type="submit" disabled={savingAction === "post"}>
                  {savingAction === "post" ? "Posting..." : "Add Investment"}
                </Button>
              </FormGrid>
            </form>
            <HelperText>
              Adds this amount to the saved investment value..
            </HelperText>
          </FormCard>

          <FormCard>
            <h3>Edit Investment</h3>
            <form onSubmit={handleEditInvestmentValue}>
              <FormGrid>
                <Field>
                  <label htmlFor="investment-edit-value">Total Investment Value</label>
                  <Input
                    id="investment-edit-value"
                    inputMode="decimal"
                    value={investmentEditValue}
                    placeholder="Enter total value"
                    onChange={(event) =>
                      setInvestmentEditValue(normaliseAmountInput(event.target.value))
                    }
                  />
                </Field>
                <Button type="submit" disabled={savingAction === "edit"}>
                  {savingAction === "edit" ? "Updating..." : "Edit Investment"}
                </Button>
              </FormGrid>
            </form>
            <HelperText>
              Updates the Investment Value card to the total amount entered here.
            </HelperText>
          </FormCard>
        </InvestmentForms>
        <HelperText>
          The Investment Value card shows saved investment plus inward minus outward.
        </HelperText>
      </Panel>

      <SectionTitle>Daily Investment Register</SectionTitle>

      <FilterBar>
        <FilterGroup>
          <label htmlFor="inventory-filter">Filter</label>
          <Select
            id="inventory-filter"
            value={dateFilter}
            onChange={(event) => setDateFilter(event.target.value)}
          >
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="date">Date</option>
            <option value="month">This Month</option>
            <option value="custom">Custom</option>
          </Select>
          {dateFilter === "date" && (
            <Input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              style={{ width: 160 }}
            />
          )}
          {dateFilter === "custom" && (
            <>
              <Input
                type="date"
                value={customStart}
                onChange={(event) => setCustomStart(event.target.value)}
                style={{ width: 160 }}
              />
              <Input
                type="date"
                value={customEnd}
                onChange={(event) => setCustomEnd(event.target.value)}
                style={{ width: 160 }}
              />
            </>
          )}
        </FilterGroup>

        <FilterGroup>
          <Input
            type="text"
            placeholder="Search Loan / Customer / Particulars"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ minWidth: 260 }}
          />
        </FilterGroup>
      </FilterBar>

      {filteredDailyRegister.length === 0 ? (
        <EmptyState>No inventory records found. Save an Investment Value or add billing transactions.</EmptyState>
      ) : (
        filteredDailyRegister.map((day) => (
          <DaySection key={day.dateKey} $isToday={day.dateKey === todayKey()}>
            <DayHeader $isToday={day.dateKey === todayKey()}>
              <div>
                <h3>{formatDisplayDate(day.dateKey)}</h3>
                <span>
                  {day.isFirstDay
                    ? "Opening starts from saved inventory value"
                    : "Opening carried from previous total value"}
                </span>
              </div>

              <DayMetrics>
                <MetricPill>
                  Opening <strong>{formatCurrencyINR(day.opening)}</strong>
                </MetricPill>
                <MetricPill $tone="success">
                  Inward <strong>{formatCurrencyINR(day.inwardTotal)}</strong>
                </MetricPill>
                <MetricPill $tone="danger">
                  Outward <strong>{formatCurrencyINR(day.outwardTotal)}</strong>
                </MetricPill>
                <MetricPill>
                  Closing Balance <strong>{formatCurrencyINR(day.closing)}</strong>
                </MetricPill>
              </DayMetrics>
            </DayHeader>

            {day.transactions.length === 0 ? (
              <EmptyState style={{ margin: 16 }}>
                No inward or outward billing movement for this day.
              </EmptyState>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Type</Th>
                        <Th>Loan ID</Th>
                        <Th>Customer</Th>
                        <Th>Particulars</Th>
                        <Th style={{ textAlign: "right" }}>Inward</Th>
                        <Th style={{ textAlign: "right" }}>Outward</Th>
                        <Th style={{ textAlign: "right" }}>Balance</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {day.transactions.map((row) => (
                        <tr key={row.id}>
                          <Td>
                            <TypeBadge $variant={row.type === "Inward" ? "inward" : "outward"}>
                              {row.type}
                            </TypeBadge>
                          </Td>
                          <Td>{row.loanId}</Td>
                          <Td>{row.customerName}</Td>
                          <Td>{row.particulars}</Td>
                          <Td style={{ textAlign: "right" }}>
                            {row.inward != null ? formatCurrencyINR(row.inward) : "-"}
                          </Td>
                          <Td style={{ textAlign: "right" }}>
                            {row.outward != null ? formatCurrencyINR(row.outward) : "-"}
                          </Td>
                          <Td style={{ textAlign: "right", fontWeight: 700 }}>
                            {formatCurrencyINR(row.runningBalance)}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableContainer>

                <MobileContainer>
                  {day.transactions.map((row) => (
                    <MobileCard key={row.id}>
                      <Row>
                        <Label>Type</Label>
                        <Val>
                          <TypeBadge $variant={row.type === "Inward" ? "inward" : "outward"}>
                            {row.type}
                          </TypeBadge>
                        </Val>
                      </Row>
                      <Row>
                        <Label>Loan / Customer</Label>
                        <Val>
                          {row.loanId}
                          <br />
                          <span style={{ fontWeight: 500 }}>{row.customerName}</span>
                        </Val>
                      </Row>
                      <Row>
                        <Label>Particulars</Label>
                        <Val>{row.particulars}</Val>
                      </Row>
                      <Row>
                        <Label>Inward</Label>
                        <Val>{row.inward != null ? formatCurrencyINR(row.inward) : "-"}</Val>
                      </Row>
                      <Row>
                        <Label>Outward</Label>
                        <Val>{row.outward != null ? formatCurrencyINR(row.outward) : "-"}</Val>
                      </Row>
                      <Row>
                        <Label>Balance</Label>
                        <Val>{formatCurrencyINR(row.runningBalance)}</Val>
                      </Row>
                    </MobileCard>
                  ))}
                </MobileContainer>
              </>
            )}
          </DaySection>
        ))
      )}
    </Wrapper>
  );
}
